import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Backdrop, Box, Button, CircularProgress, Grid } from "@material-ui/core";
import { useAlert } from "react-alert";

import { AuctionListLimit, GenerateReceipt } from "src/adminModel/auctions";

import {
  InvoiceCreate,
  InvoiceBulkCreate,
  InvoiceFindAll,
  InvoiceSend,
  InvoiceFindOne,
  InvoiceAddPayment,
  InvoiceGetPayments,
  InvoiceRemoveLots,
  InvoiceCancel,
  invoiceUpdate,
} from "src/adminModel/invoice";

import ReviewInvoice from "./ReviewInvoice";

import { useInvoiceStyles } from "./styles";
import { buildInvoicePdf, buildRenderPayload, normalizeInvoiceItems } from "./invoicePdf";

import CreateInvoiceForm from "./components/CreateInvoiceForm";
import InvoiceHistoryTable from "./components/InvoiceHistoryTable";
import InvoiceRowMenu from "./components/InvoiceRowMenu";
import SendAllProgress from "./components/SendAllProgress";
import BulkInvoiceFlow from "./components/BulkInvoiceFlow";

import PaymentDialog from "./dialogs/PaymentDialog";
import ManageLotsDialog from "./dialogs/ManageLotsDialog";
import CustomerDialog from "./dialogs/CustomerDialog";

const EMPTY_ADDON = { discription: "", price: 0 };

const emptyPaymentForm = () => ({
  amount: "",
  method: "",
  reference: "",
  remarks: "",
  date: new Date().toISOString().slice(0, 16),
});

/**
 * Invoice view -- orchestrator.
 *
 * This file owns the state and the API calls. Everything it renders is a
 * presentational component that takes props and calls back. That is the
 * whole split: one place to look for "what happens", separate places to
 * look for "what it looks like".
 */
const Views = ({ className, ...rest }) => {
  const classes = useInvoiceStyles();
  const alert = useAlert();

  // ============================================================
  // MODE
  // ============================================================

  const [invoiceMode, setInvoiceMode] = useState("individual");

  // ============================================================
  // BULK
  // ============================================================

  const [bulk, setBulk] = useState({
    step: 1,
    file: null,
    rows: [],
    summary: { total: 0, valid: 0, errors: 0 },
    progress: 0,
    generating: false,
    result: null,
    auction: null,
  });

  const patchBulk = (changes) => setBulk((prev) => ({ ...prev, ...changes }));

  // ============================================================
  // SHARED
  // ============================================================

  const [loading, setloading] = useState(false);
  const [auctions, setAuctions] = useState([]);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");

  const [eventId, setEventId] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
   const [bp, setBp] = useState("0.00");

  const [invoiceImageOptions, setInvoiceImageOptions] = useState({});

  // ============================================================
  // CREATE FORM
  // ============================================================

  const [customerName, setName] = useState("");
  const [clientEmail, setEmail] = useState("");
  const [lotNumberData, setLotNumber] = useState("");
  const [withImage, setWithImage] = useState(true);
  const [inputs, setInputs] = useState([{ ...EMPTY_ADDON }]);

  // ============================================================
  // PREVIEW / PDF
  // ============================================================

  const [openForm, setOpenForm] = useState(false);
  const [numberLot, setNumberLot] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const [sendAll, setSendAll] = useState({
    loading: false,
    progress: 0,
    current: 0,
    total: 0,
  });

  // ============================================================
  // ROW MENU
  // ============================================================

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuInvoice, setMenuInvoice] = useState(null);

  // ============================================================
  // PAYMENT DIALOG
  // ============================================================

  const [payment, setPayment] = useState({
    open: false,
    loading: false,
    saving: false,
    invoice: null,
    rows: [],
  });

  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm());

  const patchPayment = (changes) =>
    setPayment((prev) => ({ ...prev, ...changes }));

  const handlePaymentFormChange = (field, value) =>
    setPaymentForm((prev) => ({ ...prev, [field]: value }));

  // ============================================================
  // CUSTOMER DIALOG
  // ============================================================

  const [customer, setCustomer] = useState({
    open: false,
    loading: false,
    invoice: null,
    name: "",
    email: "",
  });

  const patchCustomer = (changes) =>
    setCustomer((prev) => ({ ...prev, ...changes }));

  // ============================================================
  // MANAGE LOTS DIALOG
  // ============================================================

  const [lots, setLots] = useState({
    open: false,
    loading: false,
    invoice: null,
    rows: [],
    removing: null,
  });

  const patchLots = (changes) => setLots((prev) => ({ ...prev, ...changes }));

  // ============================================================
  // LOAD AUCTIONS
  // ============================================================

  useEffect(() => {
    async function fetchData() {
      setloading(true);

      try {
        const results = await AuctionListLimit();
   
        setAuctions(results.data);
      } catch (error) {
        console.error(error);
      } finally {
        setloading(false);
      }
    }

    fetchData();
  }, []);

  // ============================================================
  // INVOICE HISTORY
  // ============================================================

  const loadInvoiceHistory = async (auctionId, keyword = search) => {
    if (!auctionId) {
      setData([]);
      return;
    }

    try {
      setloading(true);

      const results = await InvoiceFindAll(auctionId, keyword);
      
      setData(results && results.S ? results.data || [] : []);
    } catch (error) {
      console.error("Invoice History Error:", error);
      setData([]);
    } finally {
      setloading(false);
    }
  };

  // Debounced search.
  //
  // The old code called setSearch then reloaded immediately. setSearch is
  // asynchronous, so the request carried the PREVIOUS value -- results were
  // always one keystroke behind, and it fired one call per character.
  useEffect(() => {
    if (!eventId) {
      return undefined;
    }

    const timer = setTimeout(() => {
      loadInvoiceHistory(eventId, search);
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, eventId]);

  // ============================================================
  // ROW MENU
  // ============================================================

  const handleOpenMenu = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setMenuInvoice(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuInvoice(null);
  };

  // ============================================================
  // PAYMENT
  // ============================================================

  const handleOpenPayment = async (row) => {
    try {
      handleCloseMenu();

      if (!row?.id) {
        alert.error("Invoice ID not found.");
        return;
      }

      patchPayment({ open: true, loading: true, invoice: row, rows: [] });
      setPaymentForm(emptyPaymentForm());

      const result = await InvoiceGetPayments(row.id);

      if (result?.S && result?.data) {
        patchPayment({
          invoice: { ...row, ...(result.data.invoice || {}) },
          rows: result.data.payments || [],
        });
      } else {
        alert.error(
          result?.msg || result?.errors || "Unable to load payment history."
        );
      }
    } catch (error) {
      console.error("Payment Details Error:", error);
      alert.error(error?.message || "Unable to load payment details.");
    } finally {
      patchPayment({ loading: false });
    }
  };

  const handleClosePayment = () => {
    setPayment({
      open: false,
      loading: false,
      saving: false,
      invoice: null,
      rows: [],
    });

    setPaymentForm(emptyPaymentForm());
  };

  const handleSavePayment = async () => {
    try {
      if (!payment.invoice?.id) {
        alert.error("Invoice not found.");
        return;
      }

      const amount = Number(paymentForm.amount);
      const currentBalance = Number(payment.invoice.balance_amount || 0);

      if (!amount || amount <= 0) {
        alert.error("Enter a valid payment amount.");
        return;
      }

      if (amount > currentBalance) {
        alert.error(
          `Payment cannot be greater than the remaining balance of ${currentBalance.toFixed(
            2
          )}.`
        );
        return;
      }

      patchPayment({ saving: true });

      const result = await InvoiceAddPayment(payment.invoice.id, {
        amount,
        paymentMethod: paymentForm.method,
        referenceNo: paymentForm.reference,
        remarks: paymentForm.remarks,
        paymentDate: paymentForm.date,
      });

      if (!result?.S) {
        alert.error(result?.msg || result?.errors || "Unable to add payment.");
        return;
      }

      alert.success(result.msg || "Payment successfully added.");

      const details = await InvoiceGetPayments(payment.invoice.id);

      if (details?.S && details?.data) {
        patchPayment({
          invoice: { ...payment.invoice, ...(details.data.invoice || {}) },
          rows: details.data.payments || [],
        });
      }

      if (eventId) {
        await loadInvoiceHistory(eventId);
      }

      setPaymentForm(emptyPaymentForm());
    } catch (error) {
      console.error("Save Payment Error:", error);
      alert.error(error?.message || "Unable to save payment.");
    } finally {
      patchPayment({ saving: false });
    }
  };

  // ============================================================
  // CUSTOMER NAME / EMAIL
  // ============================================================

  const handleOpenCustomer = async (row) => {
    try {
      handleCloseMenu();

      if (!row?.id) {
        alert.error("Invoice ID not found.");
        return;
      }

      patchCustomer({ open: true, loading: true, invoice: row });

      const result = await InvoiceGetPayments(row.id);

      if (result?.S && result?.data) {
        patchCustomer({
          invoice: { ...row, ...(result.data.invoice || {}) },
          name: result.data.invoice.name || "",
          email: result.data.invoice.email || "",
        });
      } else {
        alert.error(
          result?.msg || result?.errors || "Unable to load invoice details."
        );
      }
    } catch (error) {
      console.error("Customer Details Error:", error);
      alert.error(error?.message || "Unable to load invoice details.");
    } finally {
      patchCustomer({ loading: false });
    }
  };

  const handleCloseCustomer = () =>
    setCustomer({
      open: false,
      loading: false,
      invoice: null,
      name: "",
      email: "",
    });

  const handleSaveCustomer = async () => {
    try {
      if (!customer.name || !customer.name.trim()) {
        alert.error("Please enter client name.");
        return;
      }

      const response = await invoiceUpdate({
        invoiceId: customer.invoice.id,
        name: customer.name.trim(),
        email: customer.email ? customer.email.trim() : "",
      });

      if (!response || response.S !== true) {
        alert.error(
          response?.errors || response?.message || "Unable to update invoice."
        );
        return;
      }

      alert.success("Invoice updated successfully.");

      await loadInvoiceHistory(eventId);
    } catch (error) {
      console.error("Update INVOICE ERROR:", error);
      alert.error(error?.message || "Unable to update invoice.");
    } finally {
      handleCloseCustomer();
    }
  };

  // ============================================================
  // MANAGE LOTS
  // ============================================================

  const handleOpenLots = async (row) => {
    try {
      handleCloseMenu();

      if (!row?.id) {
        alert.error("Invoice ID not found.");
        return;
      }

      patchLots({ open: true, loading: true, invoice: row, rows: [] });

      const result = await InvoiceFindOne(row.id);

      if (result?.S && result?.data) {
        patchLots({
          invoice: { ...row, ...(result.data.invoice || {}) },
          rows: result.data.items || [],
        });
      } else {
        alert.error(result?.errors || "Unable to load invoice lots.");
      }
    } catch (error) {
      console.error("Manage Lots Error:", error);
      alert.error(error?.message || "Unable to load invoice lots.");
    } finally {
      patchLots({ loading: false });
    }
  };

  const handleCloseLots = () =>
    setLots({
      open: false,
      loading: false,
      invoice: null,
      rows: [],
      removing: null,
    });

  const handleRemoveLot = async (lotNumber) => {
    try {
      if (!lots.invoice?.id) {
        alert.error("Invoice not found.");
        return;
      }

      const confirmed = window.confirm(
        `Remove lot ${lotNumber} from invoice ${lots.invoice.invoice_no || ""}?\n\n` +
          `The invoice total will be recalculated.`
      );

      if (!confirmed) {
        return;
      }

      patchLots({ removing: lotNumber });

      const result = await InvoiceRemoveLots(lots.invoice.id, [lotNumber]);

      if (!result?.S) {
        // The backend refuses in two cases and explains why: removing every
        // lot, and dropping the total below what is already paid.
        alert.error(result?.errors || "Unable to remove the lot.");
        return;
      }

      alert.success(result.msg || "Lot removed.");

      // Take the new figures from the response. The server recalculates
      // from the remaining rows; a second calculation here can drift.
      setLots((prev) => ({
        ...prev,
        invoice: {
          ...prev.invoice,
          total_amount: result.total_amount,
          balance_amount: result.balance_amount,
          payment_status: result.payment_status,
        },
        rows: prev.rows.filter(
          (item) => String(item.lot_number) !== String(lotNumber)
        ),
      }));

      if (eventId) {
        await loadInvoiceHistory(eventId);
      }
    } catch (error) {
      console.error("Remove Lot Error:", error);
      alert.error(error?.message || "Unable to remove the lot.");
    } finally {
      patchLots({ removing: null });
    }
  };

  // ============================================================
  // CANCEL INVOICE
  // ============================================================

  const handleCancelInvoice = async (row) => {
    try {
      handleCloseMenu();

      if (!row?.id) {
        alert.error("Invoice ID not found.");
        return;
      }

      const confirmed = window.confirm(
        `Cancel invoice ${row.invoice_no || ""}?\n\n` +
          `It will stay in the list as Cancelled, but it can no longer be ` +
          `edited or sent.`
      );

      if (!confirmed) {
        return;
      }

      setloading(true);

      const result = await InvoiceCancel(row.id);

      if (!result?.S) {
        // The backend refuses when the invoice already has payments and
        // says how much was paid. Show that, not a generic message.
        alert.error(result?.errors || "Unable to cancel the invoice.");
        return;
      }

      alert.success(result.msg || "Invoice cancelled.");

      await loadInvoiceHistory(eventId);
    } catch (error) {
      console.error("Cancel Invoice Error:", error);
      alert.error(error?.message || "Unable to cancel the invoice.");
    } finally {
      setloading(false);
    }
  };

  // ============================================================
  // AUCTION PICKER
  // ============================================================

  const handleSelectAuction = async (e, v) => {
    if (!v) {
      setEventId("");
      setEventName("");
      setEventDate("");
      setData([]);
      return;
    }

    setEventName(v.auction_name);
    setEventId(v.id);
    setEventDate(v.date_start);

    await loadInvoiceHistory(v.id);
  };

  // ============================================================
  // PREVIEW
  // ============================================================

  const handleOpenPreview = async (row, includeImage) => {
    try {
      setPdfGenerating(false);
    
      const rawLotNumbers = row.lot_number || row.lot_numbers || "";

      const formattedLotNumbers = Array.isArray(rawLotNumbers)
        ? rawLotNumbers
            .map((lot) =>
              typeof lot === "object"
                ? lot.lot_number || lot.lotNumber || ""
                : String(lot)
            )
            .map((lot) => String(lot).trim())
            .filter(Boolean)
            .join(",")
        : String(rawLotNumbers || "")
            .replace(/\n/g, ",")
            .split(",")
            .map((lot) => lot.trim())
            .filter(Boolean)
            .join(",");

      const client =
        row.client_name || row.customer_name || row.name || "";

      const title = row.event_name || row.auction_name || eventName;

      setNumberLot(formattedLotNumbers);
      setName(client);
      setEmail(row.email || row.customer_email || "");
      setBp(
        row.buyer_premium !== undefined &&
        row.buyer_premium !== null &&
        row.buyer_premium !== ""
          ? String(row.buyer_premium)
          : "0.00"
      );

      setWithImage(Boolean(includeImage));

      if (title) {
        setEventName(title);
      }

      if (row.id) {
        setloading(true);

        const response = await InvoiceFindOne(row.id);
        
        if (response?.S && response?.data) {
          setSelectedInvoice({
            ...(response.data.invoice || {}),
            items: normalizeInvoiceItems(response.data.items || []),
            addons: response.data.addons || [],
          });
        } else {
          setSelectedInvoice(null);
        }

        setloading(false);
      }

      // Reuse the saved receipt number when the inputs are unchanged, so
      // opening the same preview twice does not burn a new number.
      const currentData = {
        eventName: title,
        name: client,
        lotNumber: row.lot_number || row.lot_numbers || "",
      };

      let savedData = null;

      try {
        const raw = localStorage.getItem("receipt_data");
        savedData = raw ? JSON.parse(raw) : null;
      } catch {
        savedData = null;
      }

      const inputsMatch =
        savedData &&
        savedData.eventName === currentData.eventName &&
        savedData.name === currentData.name &&
        savedData.lotNumber === currentData.lotNumber &&
        savedData.receiptNumber;

      if (inputsMatch) {
        setReceiptNumber(savedData.receiptNumber);
      } else {
        setloading(true);

        try {
          const response = await GenerateReceipt();

          if (response.success) {
            setReceiptNumber(response.number);

            localStorage.setItem(
              "receipt_data",
              JSON.stringify({ ...currentData, receiptNumber: response.number })
            );
          }
        } catch (error) {
          console.error(error);
        } finally {
          setloading(false);
        }
      }

      setOpenForm(true);
    } catch (error) {
      console.error("PREVIEW INVOICE ERROR:", error);
      setloading(false);
      alert.error("Failed to load invoice details.");
    }
  };

  // ============================================================
  // CREATE INVOICE
  // ============================================================

  const handleCreateInvoice = async () => {
    try {
      if (!eventId) {
        alert.error("Please select an auction.");
        return;
      }

      if (!customerName || !customerName.trim()) {
        alert.error("Please enter customer name.");
        return;
      }

      if (!lotNumberData || !lotNumberData.trim()) {
        alert.error("Please enter lot numbers.");
        return;
      }

      const lotNumbers = lotNumberData
        .split(/[\n,]+/)
        .map((lot) => String(lot).trim())
        .filter(Boolean);

      if (lotNumbers.length === 0) {
        alert.error("Please enter valid lot numbers.");
        return;
      }

      const addons = inputs
        .filter(
          (item) =>
            item && item.discription && String(item.discription).trim() !== ""
        )
        .map((item) => ({
          discription: String(item.discription).trim(),
          price: Number(item.price) || 0,
        }));

      setloading(true);

      const response = await InvoiceCreate({
        auctionId: eventId,
        name: customerName.trim(),
        email: clientEmail ? clientEmail.trim() : "",
        lotNumbers,
        buyer_premium: Number(bp) || 0,
        addons,
        withImage,
      });

      if (!response || response.S !== true) {
        console.error("CREATE INVOICE FAILED:", response);

        alert.error(
          response?.errors || response?.message || "Unable to create invoice."
        );
        return;
      }

      alert.success(
        response.invoice_no
          ? `Invoice ${response.invoice_no} created successfully.`
          : "Invoice created successfully."
      );

      setName("");
      setEmail("");
      setLotNumber("");
      setBp("0.00");
      setInputs([{ ...EMPTY_ADDON }]);
      setWithImage(true);

      await loadInvoiceHistory(eventId);
    } catch (error) {
      console.error("CREATE INVOICE ERROR:", error);

      alert.error(
        error?.response?.data?.errors ||
          error?.message ||
          "Unable to create invoice."
      );
    } finally {
      setloading(false);
    }
  };

  // ============================================================
  // ADDON ROWS
  // ============================================================

  const handleAddonChange = (index, event) => {
    const { name, value } = event.target;

    setInputs((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [name]: value };
      return next;
    });
  };

  const handleAddAddon = () => setInputs((prev) => [...prev, { ...EMPTY_ADDON }]);

  const handleDeleteAddon = (index) =>
    setInputs((prev) =>
      prev.length === 1 ? [{ ...EMPTY_ADDON }] : prev.filter((_, i) => i !== index)
    );

  // ============================================================
  // SEND
  // ============================================================

  const pushInvoiceToRenderer = (payload) => {
    setSelectedInvoice(payload.invoice);
    setName(payload.customerName);
    setEmail(payload.email);
    setNumberLot(payload.lotNumbers);
    setReceiptNumber(payload.invoiceNumber);
    setBp(
      payload?.invoice?.buyer_premium !== undefined &&
      payload?.invoice?.buyer_premium !== null &&
      payload?.invoice?.buyer_premium !== ""
        ? String(payload.invoice.buyer_premium)
        : "0.00"
    );
    setWithImage(payload.includeImage);

    if (payload.eventName) {
      setEventName(payload.eventName);
    }
  };

  const sendOneInvoice = async (row) => {
    const details = await InvoiceFindOne(row.id);

    if (!details || details.S !== true) {
      throw new Error(details?.errors || "Unable to load invoice details.");
    }

    const payload = buildRenderPayload(
      row,
      details,
      invoiceImageOptions,
      eventName
    );

    pushInvoiceToRenderer(payload);

    setPdfGenerating(true);
    setOpenForm(true);

    const base64Pdf = await buildInvoicePdf(payload.invoiceNumber);

    const response = await InvoiceSend(payload.invoiceId, base64Pdf);

    if (!response || response.S !== true) {
      throw new Error(
        response?.errors || response?.msg || "Unable to send invoice."
      );
    }

    return payload.invoiceNumber;
  };

  const handleSendInvoice = async (row) => {
    try {
      if (!row || !row.id) {
        alert.error("Invoice record not found.");
        return;
      }

      setloading(true);

      const invoiceNumber = await sendOneInvoice(row);

      await loadInvoiceHistory(eventId);

      alert.success(`Invoice ${invoiceNumber} sent successfully.`);
    } catch (error) {
      console.error("SEND INVOICE ERROR:", error);
      alert.error(error?.message || "Unable to send invoice.");
    } finally {
      setOpenForm(false);
      setSelectedInvoice(null);
      setPdfGenerating(false);
      setloading(false);
    }
  };

  const handleSendAllInvoices = async () => {
    if (!eventId) {
      alert.error("Please select an auction first.");
      return;
    }

    const pending = (data || []).filter((row) => {
      const status = String(row.status || "DRAFT").toUpperCase();
      return status !== "SENT" && status !== "CANCELLED";
    });

    if (pending.length === 0) {
      alert.success("All invoices have already been sent.");
      return;
    }

    setSendAll({ loading: true, progress: 0, current: 0, total: pending.length });

    let successCount = 0;
    let failedCount = 0;
    let processed = 0;

    for (const row of pending) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await sendOneInvoice(row);
        successCount++;
      } catch (error) {
        failedCount++;
        console.error(`SEND ALL FAILED - Invoice ${row?.id}:`, error);
      } finally {
        setOpenForm(false);
        setSelectedInvoice(null);
        setPdfGenerating(false);
        /*
        processed++;

        setSendAll((prev) => ({
          ...prev,
          current: processed,
          progress: Math.round((processed / pending.length) * 100),
        }));
  */

const currentProcessed = processed;

setSendAll((prev) => ({
  ...prev,
  current: currentProcessed,
  progress: Math.round((currentProcessed / pending.length) * 100),
}));
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    }

    // One refresh at the end. The old version reloaded the whole history
    // after EVERY invoice -- 50 invoices meant 50 extra round trips.
    try {
      await loadInvoiceHistory(eventId);
    } catch (refreshError) {
      console.error("REFRESH HISTORY ERROR:", refreshError);
    }

    if (failedCount === 0) {
      alert.success(`${successCount} invoice(s) sent successfully.`);
    } else if (successCount > 0) {
      alert.success(
        `${successCount} invoice(s) sent successfully. ${failedCount} failed.`
      );
    } else {
      alert.error("No invoices were sent.");
    }

    setSendAll({ loading: false, progress: 0, current: 0, total: 0 });
  };

  // ============================================================
  // BULK GENERATE
  // ============================================================

  const handleBulkGenerate = async () => {
    if (!bulk.auction) {
      const message = "Please select an auction first.";
      patchBulk({ result: { S: false, errors: message } });
      alert.error(message);
      return;
    }

    if (!bulk.rows.length) {
      const message = "No invoice rows found.";
      patchBulk({ result: { S: false, errors: message } });
      alert.error(message);
      return;
    }

    if (bulk.summary.errors > 0) {
      const message =
        "Please correct the Excel errors before generating invoices.";
      patchBulk({ result: { S: false, errors: message } });
      alert.error(message);
      return;
    }

    try {
      patchBulk({ generating: true, progress: 10, result: null, step: 3 });

      const invoices = bulk.rows.map((row) => ({
        name: row.customer_name || "",
        email: row.email || "",
        lotNumbers: Array.isArray(row.lot_numbers) ? row.lot_numbers : [],
        addons: Array.isArray(row.addons) ? row.addons : [],
        withImage: !!row.include_artwork,
      }));

      patchBulk({ progress: 30 });

      const results = await InvoiceBulkCreate({
        auctionId: bulk.auction.id,
        invoices,
      });

      if (!results || results.S !== true) {
        const errorMessage =
          results?.errors || results?.msg || "Unable to save bulk invoices.";

        patchBulk({
          result: { ...(results || {}), S: false, errors: errorMessage },
          progress: 100,
          generating: false,
        });

        alert.error(errorMessage);
        return;
      }

      patchBulk({ progress: 100, result: results, generating: false });

      alert.success(
        `${results.count || invoices.length} invoice(s) created successfully.`
      );

      await loadInvoiceHistory(bulk.auction.id);
    } catch (error) {
      console.error("BULK INVOICE ERROR:", error);

      const errorMessage =
        error?.response?.data?.errors ||
        error?.message ||
        "Unable to save bulk invoices.";

      patchBulk({
        progress: 100,
        result: { S: false, errors: errorMessage },
        generating: false,
      });

      alert.error(errorMessage);
    }
  };

  const handleImageChange = (id, checked) =>
    setInvoiceImageOptions((prev) => ({ ...prev, [id]: checked }));

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className={`${classes.root} ${className || ""}`} {...rest}>
      <Box className={classes.modeBar}>
        <Button
          className={classes.modeButton}
          variant={invoiceMode === "individual" ? "contained" : "outlined"}
          color="primary"
          onClick={() => {
            setInvoiceMode("individual");
            patchBulk({ step: 1 });
          }}
        >
          Individual Invoice
        </Button>

        <Button
          className={classes.modeButton}
          variant={invoiceMode === "bulk" ? "contained" : "outlined"}
          color="primary"
          onClick={() => {
            setInvoiceMode("bulk");
            patchBulk({ step: 1 });
          }}
        >
          Bulk Excel Upload
        </Button>
      </Box>

      {invoiceMode === "bulk" ? (
        <BulkInvoiceFlow
          step={bulk.step}
          file={bulk.file}
          rows={bulk.rows}
          summary={bulk.summary}
          progress={bulk.progress}
          generating={bulk.generating}
          result={bulk.result}
          auction={bulk.auction}
          auctions={auctions}
          onAuctionChange={async (auction) => {
            patchBulk({
              auction,
              file: null,
              rows: [],
              summary: { total: 0, valid: 0, errors: 0 },
              result: null,
              progress: 0,
            });

            if (auction) {
              await loadInvoiceHistory(auction.id);
            } else {
              setData([]);
            }
          }}
          onParsed={(file, rows, summary, auction) =>
            patchBulk({
              file,
              auction,
              rows,
              summary,
              result: null,
              step: 2,
            })
          }
          onBack={() => setInvoiceMode("individual")}
          onGenerate={handleBulkGenerate}
          onBackToPreview={() => patchBulk({ generating: false, step: 2 })}
          onViewHistory={async () => {
            if (bulk.auction) {
              await loadInvoiceHistory(bulk.auction.id);
            }

            setInvoiceMode("individual");
          }}
        />
      ) : (
        <Grid container spacing={3}>
          <CreateInvoiceForm
            auctions={auctions}
            lotNumberData={lotNumberData}
            onLotNumberChange={setLotNumber}
            customerName={customerName}
            onCustomerNameChange={setName}
            clientEmail={clientEmail}
            onClientEmailChange={setEmail}
            buyerPremium={bp}
            onBuyerPremiumChange={setBp}
            withImage={withImage}
            onWithImageChange={setWithImage}
            addons={inputs}
            onAddonChange={handleAddonChange}
            onAddAddon={handleAddAddon}
            onDeleteAddon={handleDeleteAddon}
            onSelectAuction={handleSelectAuction}
            onCreate={handleCreateInvoice}
          />

          <Grid item xs={12}>
            <InvoiceHistoryTable
              rows={data}
              eventId={eventId}
              eventName={eventName}
              search={search}
              onSearchChange={setSearch}
              loading={loading}
              imageOptions={invoiceImageOptions}
              onImageChange={handleImageChange}
              onSendAll={handleSendAllInvoices}
              onPreview={handleOpenPreview}
              onSend={handleSendInvoice}
              onOpenPayment={handleOpenPayment}
              onOpenMenu={handleOpenMenu}
            />
          </Grid>
        </Grid>
      )}

      <InvoiceRowMenu
        anchorEl={menuAnchor}
        invoice={menuInvoice}
        onClose={handleCloseMenu}
        onViewPayment={handleOpenPayment}
        onEditCustomer={handleOpenCustomer}
        onManageLots={handleOpenLots}
        onResend={handleSendInvoice}
        onCancel={handleCancelInvoice}
      />

      <CustomerDialog
        open={customer.open}
        loading={customer.loading}
        invoice={customer.invoice}
        name={customer.name}
        email={customer.email}
        onNameChange={(value) => patchCustomer({ name: value })}
        onEmailChange={(value) => patchCustomer({ email: value })}
        onSave={handleSaveCustomer}
        onClose={handleCloseCustomer}
      />

      <ManageLotsDialog
        open={lots.open}
        loading={lots.loading}
        invoice={lots.invoice}
        rows={lots.rows}
        removing={lots.removing}
        onRemove={handleRemoveLot}
        onClose={handleCloseLots}
      />

      <PaymentDialog
        open={payment.open}
        loading={payment.loading}
        saving={payment.saving}
        invoice={payment.invoice}
        payments={payment.rows}
        form={paymentForm}
        onFormChange={handlePaymentFormChange}
        onSave={handleSavePayment}
        onClose={handleClosePayment}
      />

      {openForm && (
        <ReviewInvoice
          status={openForm}
          handleClose={() => setOpenForm(false)}
          eventId={eventId || null}
          lotNumberData={numberLot}
          customerName={customerName}
          EventName={eventName}
          Email={clientEmail}
          receiptNumber={selectedInvoice?.invoice_no || receiptNumber}
          eventDate={eventDate}
          withImage={withImage}
          invoice={selectedInvoice}
          hidePreview={pdfGenerating === true}
          addons={selectedInvoice?.addons || inputs}
          buyer_premium={bp}

        />
      )}

      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <SendAllProgress
        open={sendAll.loading}
        progress={sendAll.progress}
        current={sendAll.current}
        total={sendAll.total}
      />
    </div>
  );
};

Views.propTypes = {
  className: PropTypes.string,
};

export default Views;
